import { useMemo, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  useTokens,
  SectionContextProvider,
  EditableToken,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import {
  formatDisplayPhone,
  type OfficeHourEntry,
} from './PropertyInfoPanel';
import { listOfficeHours } from './Footer';
import { contrastRatio } from '../utils/luminance';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    text: {
      label: 'Phone',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

// ============================================================================
// Component
// ============================================================================

interface ContactFormProps {
  /**
   * Unique prefix so each page's overrides are stored independently. Falls
   * back to the canonical slot name so the component works standalone and on
   * the Contact Us page (which sets no prefix).
   */
  tokenPrefix?: string;
  /**
   * Default form block to render when the section has no override. The Contact
   * Us page uses the lead form; the Schedule A Tour page passes the tour
   * scheduler.
   */
  defaultFormBlock?: string;
}

export function ContactForm({ tokenPrefix, defaultFormBlock }: ContactFormProps = {}) {
  const prefix = tokenPrefix || 'contactform';
  const formBlockPath = `${prefix}.form_block`;
  const talkLabelPath = `${prefix}.talk_label`;
  const talkBlurbPath = `${prefix}.talk_blurb`;
  const hoursLabelPath = `${prefix}.hours_label`;
  const officeLabelPath = `${prefix}.office_label`;
  const addressPath = `${prefix}.address`;
  const phonePath = `${prefix}.phone`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: defaultFormBlock || 'multihub-lead-form',
  });

  const addressLink = useSlotGroup(addressPath, addressLinkDef);
  const phoneLink = useSlotGroup(phonePath, phoneLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as
    | string
    | null
    | undefined;
  const propertyPhone = usePropertyField('property_phone') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(`${prefix}.office_hours`, {
    collection: 'office_hours',
  });
  const dayHours = officeHours.length > 0 ? listOfficeHours(officeHours) : [];

  // Column titles: use the primary color when it's dark enough to read as a
  // heading on the light section background, otherwise fall back to black.
  const tokens = useTokens();
  const titleColorStyle = useMemo<CSSProperties>(() => {
    const primary = tokens?.color?.primary;
    const sectionBg = '#f4f3f1';
    const color = primary && contrastRatio(primary, sectionBg) >= 4.5 ? primary : '#000000';
    return { ['--cf-title-color' as string]: color } as CSSProperties;
  }, [tokens]);

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const addressText = addressLink.text || '';
  const phoneHref =
    toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay =
    formatDisplayPhone(phoneLink.text) ||
    formatDisplayPhone(callTrackingPhone) ||
    formatDisplayPhone(propertyPhone) ||
    '(555) 555-5555';

  return (
    <section className="cf-section" style={titleColorStyle}>
      <div className="cf-container">
        <div className="cf-grid">
          {/* ── Let's Talk + inline lead form ── */}
          <div className="cf-col cf-col--talk">
            <RichTextContent path={talkLabelPath} as="h2" className="cf-col-title" />
            <RichTextContent path={talkBlurbPath} as="div" className="cf-talk-blurb" />
            <div className="cf-form-wrap">
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-empty-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>
          </div>

          {/* ── Office Hours + Leasing Office stacked ── */}
          <div className="cf-side">
          {/* ── Office Hours ── */}
          <div className="cf-col cf-col--hours">
            <RichTextContent path={hoursLabelPath} as="h2" className="cf-col-title" />
            {dayHours.length > 0 ? (
              <dl className="cf-hours">
                {dayHours.map((entry, i) => (
                  <div key={i} className="cf-hours-row">
                    <dt className="cf-hours-day">{entry.label}</dt>
                    <dd className="cf-hours-time">{entry.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="cf-empty">Hours coming soon.</p>
            )}
          </div>

          {/* ── Leasing Office ── */}
          <div className="cf-col cf-col--office">
            <RichTextContent path={officeLabelPath} as="h2" className="cf-col-title" />
            {phoneHref && (
              <EditableSlotGroup groupPath={phonePath} definition={phoneLinkDef}>
                <a
                  href={phoneHref}
                  target={phoneLink._target || '_self'}
                  className="dniphone dniphonehref cf-office-phone"
                >
                  {phoneDisplay}
                </a>
              </EditableSlotGroup>
            )}
            {addressText && (
              <EditableSlotGroup groupPath={addressPath} definition={addressLinkDef}>
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="cf-office-address"
                >
                  {addressText}
                </a>
              </EditableSlotGroup>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static canonical paths so the source-level
          linter can match each schema key. The visible wrappers above use the
          per-instance dynamic paths built from `prefix`. These never render. */}
      <EditableToken path="contactform.form_block" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <RichTextContent path="contactform.talk_label" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.talk_blurb" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.hours_label" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.office_label" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="contactform.address" definition={addressLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="contactform.phone" definition={phoneLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        .cf-section {
          background: #f4f3f1;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 5rem 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }
        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: start;
        }
        @media (min-width: 900px) {
          .cf-grid {
            grid-template-columns: 1.6fr 1fr;
            gap: 2.5rem;
          }
        }

        /* Office Hours + Leasing Office stack within the side column */
        .cf-side {
          display: flex;
          flex-direction: column;
          gap: 3rem;
        }

        .cf-col {
          display: flex;
          flex-direction: column;
        }
        .cf-col-title {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 500;
          line-height: 1.15;
          color: var(--cf-title-color, #000000);
          text-align: center;
          margin: 0 0 1.25rem;
        }
        .cf-col-title p { margin: 0; }
        @media (min-width: 900px) {
          .cf-col--hours .cf-col-title,
          .cf-col--office .cf-col-title {
            text-align: left;
          }
        }

        /* ── Let's Talk ── */
        .cf-col--talk { align-items: center; text-align: center; }
        .cf-talk-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.55;
          color: var(--vibe-color-text, #342e1f);
          max-width: 26rem;
          margin: 0 0 1.75rem;
        }
        .cf-talk-blurb p { margin: 0; }
        .cf-form-wrap {
          width: 100%;
          max-width: 40rem;
        }
        /* Hide the lead-form block's own title ("Contact Us") — the section
           already renders its "Let's Talk" heading above the form, so the
           block heading is a duplicate. Scoped to a direct-child <h3> so the
           nested success-state heading is unaffected. */
        .cf-form-wrap .mh-lead-form-card > h3 {
          display: none;
        }
        /* Make the lead-form's buttons adopt the header "Resident Portal"
           link identity (.header-apply-btn): a flat secondary fill with
           sharp corners, an uppercase letter-spaced bold label, and a clean
           color-swap to the primary color on hover (no lift/shadow). The
           block paints its buttons with inline styles, so these overrides
           use !important to win. Full width is kept to fit the form column. */
        .cf-form-wrap .mh-lead-form-submit-button,
        .cf-form-wrap .mh-lead-form-success-cta {
          padding: 0.9rem 1.5rem !important;
          font-family: var(--vibe-font-body) !important;
          font-size: 0.72rem !important;
          font-weight: 700 !important;
          letter-spacing: 0.1em !important;
          text-transform: uppercase !important;
          color: var(--vibe-color-text-on-secondary) !important;
          background: var(--vibe-color-secondary) !important;
          border: none !important;
          border-radius: 0 !important;
          transition: background-color 0.3s ease, color 0.3s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover,
        .cf-form-wrap .mh-lead-form-success-cta:hover,
        .cf-form-wrap .mh-lead-form-success-cta:focus {
          background: var(--vibe-color-primary) !important;
          color: var(--vibe-color-text-on-primary) !important;
        }
        .cf-form-empty-card {
          background: var(--vibe-color-background, #fff);
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
        }

        /* ── Office Hours ── */
        /* Mirrors the footer's hours list: full day names sorted Mon→Sun in a
           spaced-apart row (day left, time right), with the footer's typography
           and row spacing. Text stays dark for the light section background. */
        .cf-hours {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          margin: 0;
          /* In the single-column layout (below 900px, including tablet) the
             list would otherwise span the full section width, pushing the
             times to the far edge with a large gap. Cap the width and center
             it so the day/time pairs stay close together. */
          width: 100%;
          max-width: 24rem;
          align-self: center;
        }
        @media (min-width: 900px) {
          .cf-hours { max-width: none; align-self: stretch; }
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.4;
          color: var(--vibe-color-text, #342e1f);
        }
        @media (min-width: 768px) {
          .cf-hours-row { font-size: 1rem; }
        }
        .cf-hours-day,
        .cf-hours-time {
          margin: 0;
        }

        /* ── Leasing Office ── */
        .cf-col--office {
          align-items: center;
          text-align: center;
          gap: 1.25rem;
        }
        @media (min-width: 900px) {
          .cf-col--office { align-items: flex-start; text-align: left; }
        }
        .cf-office-phone,
        .cf-office-address {
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          line-height: 1.5;
          color: var(--vibe-color-text, #342e1f);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .cf-office-phone:hover,
        .cf-office-phone:focus-visible,
        .cf-office-address:hover,
        .cf-office-address:focus-visible {
          color: var(--vibe-color-primary, #b28c59);
        }

        .cf-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.talk_label': {
      label: "Let's Talk Heading",
      type: 'richtext',
      defaultValue: "<p>Let's Talk</p>",
    },
    'contactform.talk_blurb': {
      label: "Let's Talk Blurb",
      type: 'richtext',
      defaultValue:
        '<p>Give us a call or fill out the form to find out how you can start living at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'contactform.hours_label': {
      label: 'Office Hours Heading',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'contactform.office_label': {
      label: 'Leasing Office Heading',
      type: 'richtext',
      defaultValue: '<p>Leasing Office</p>',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
    'contactform.phone': phoneLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
